import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Megaphone, BellRing, Users, TrendingUp, Phone, Mail, MessageSquare,
  AlertTriangle, Sparkles, Upload, FileText, Plus, Star, Search, X,
  ChevronRight, Activity, DollarSign, BarChart3, Target, Link2,
  CheckCircle2, Filter, Copy, Trash2, Eye, Loader2, Calendar, Image as ImageIcon, RefreshCcw,
  ClipboardCheck, History as HistoryIcon, User as UserIcon,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../../utils/api';
import KanbanLeadsPipeline, { PreCalModal } from '../../components/KanbanLeadsPipeline';
import CerebroIA from '../../components/CerebroIA';
import HistorialLeads from '../../components/HistorialLeads';
import BIAnalytics from '../../components/BIAnalytics';

// Formato CLP (chileno, sin decimales)
const fmtCLP = (n) => {
  if (n == null || isNaN(n)) return '$ 0';
  return '$ ' + Number(n).toLocaleString('es-CL', { maximumFractionDigits: 0 });
};
const fmtCLPCorto = (n) => {
  if (n == null || isNaN(n)) return '$0';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return '$' + (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(v) >= 1_000) return '$' + Math.round(v / 1_000) + 'K';
  return '$' + Math.round(v).toLocaleString('es-CL');
};

const ETAPAS = ['Por Contactar', 'Contactado', 'Sin Respuesta', 'En Precalificación', 'Reunión Socios', 'Aceptado', 'Descartado', 'Recuperado', 'Perdido'];
const ETAPA_COLOR = {
  'Por Contactar':      { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500' },
  'Contactado':         { bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     dot: 'bg-sky-500' },
  'Sin Respuesta':      { bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  dot: 'bg-orange-500' },
  'En Precalificación': { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  dot: 'bg-violet-500' },
  'Reunión Socios':     { bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  dot: 'bg-indigo-500' },
  'Aceptado':           { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  'Descartado':         { bg: 'bg-zinc-100',   text: 'text-zinc-600',    border: 'border-zinc-200',    dot: 'bg-zinc-500' },
  'Recuperado':         { bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200',    dot: 'bg-teal-500' },
  'Perdido':            { bg: 'bg-slate-100',  text: 'text-slate-600',   border: 'border-slate-200',   dot: 'bg-slate-400' }
};
const MOTIVOS_PRESET = ['Urgencia', 'Capacidad de pago', 'Claridad del problema', 'Intención de compra', 'Empresa B2B', 'Fit técnico', 'Decisión inmediata', 'Necesidad recurrente'];

const Marketing = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [tab, setTab] = useState('pipeline');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [embudo, setEmbudo] = useState(null);
  const [prospectos, setProspectos] = useState([]);
  const [campanas, setCampanas] = useState([]);
  const [search, setSearch] = useState('');
  const [showFichaId, setShowFichaId] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [showWebhookInfo, setShowWebhookInfo] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [periodo, setPeriodo] = useState('all'); // 7d / 30d / 90d / year / all

  // Polling control para alertas
  const lastSeenRef = useRef(new Set()); // ids ya vistos
  const audioCtxRef = useRef(null);
  const soundEnabled = useRef(true);

  // === AUDIO ALERT (Web Audio API beep) ===
  const playBeep = useCallback(() => {
    if (!soundEnabled.current) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.value = 0.0001;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const start = ctx.currentTime + i * 0.25;
        gain.gain.exponentialRampToValueAtTime(0.18, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
        osc.start(start);
        osc.stop(start + 0.22);
      });
    } catch (e) { /* silencioso */ }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const periodoParam = periodo && periodo !== 'all' ? `?periodo=${periodo}` : '';
      // Promise.allSettled: si un endpoint falla, los demás siguen renderizando.
      // Antes usaba Promise.all → un solo fallo escondía TODO el módulo (bug reportado en PROD).
      const [rDash, rEmb, rPro, rCam] = await Promise.allSettled([
        api.get(`/marketing/dashboard${periodoParam}`),
        api.get(`/marketing/embudo${periodoParam}`),
        api.get('/marketing/prospectos'),
        api.get('/marketing/campanas')
      ]);

      const fallidos = [];
      if (rDash.status === 'fulfilled') setDashboard(rDash.value.data);
      else fallidos.push({ endpoint: 'dashboard', reason: rDash.reason });

      if (rEmb.status === 'fulfilled') setEmbudo(rEmb.value.data);
      else fallidos.push({ endpoint: 'embudo', reason: rEmb.reason });

      if (rPro.status === 'fulfilled') {
        setProspectos(rPro.value.data.prospectos || []);
        const seen = new Set((rPro.value.data.prospectos || []).map(p => p.id));
        lastSeenRef.current = seen;
      } else {
        fallidos.push({ endpoint: 'prospectos', reason: rPro.reason });
      }

      if (rCam.status === 'fulfilled') setCampanas(rCam.value.data.campanas || []);
      else fallidos.push({ endpoint: 'campanas', reason: rCam.reason });

      // Si algo falló, mostrar toast con el detalle sin bloquear el resto del render
      if (fallidos.length > 0) {
        console.error('[Marketing] Endpoints con error:', fallidos);
        fallidos.forEach(({ endpoint, reason }) => {
          const cfg = reason?.config || {};
          const resp = reason?.response;
          const detalles = [
            resp?.status ? `HTTP ${resp.status}` : (reason?.code || 'network'),
            cfg?.url || `/${endpoint}`,
            formatErrorMessage(reason),
          ].filter(Boolean).join(' · ');
          toast.error(`Marketing · ${endpoint}: ${detalles}`, { duration: 10000 });
        });
      }
    } catch (e) {
      // Este catch ya casi no debería ejecutarse porque allSettled no rechaza.
      console.error('[Marketing] Error inesperado en fetchAll:', e);
      toast.error(`Error inesperado en Marketing: ${formatErrorMessage(e)}`, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // === POLLING NUEVOS LEADS ===
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const res = await api.get('/marketing/prospectos/nuevos?desde_minutos=2');
        const nuevos = (res.data.leads || []).filter(l => !lastSeenRef.current.has(l.id));
        if (nuevos.length > 0) {
          nuevos.forEach(n => lastSeenRef.current.add(n.id));
          playBeep();
          nuevos.forEach(n => {
            toast.success(
              `🔔 Nuevo lead: ${n.nombre}${n.urgencia_critica ? ' · URGENCIA CRÍTICA' : ''}`,
              { description: `${n.empresa || ''} · ${n.origen || 'Webhook'}`, duration: 8000 }
            );
          });
          fetchAll();
        }
      } catch (e) { /* silencioso */ }
    }, 30000);
    return () => clearInterval(id);
  }, [playBeep, fetchAll]);

  const fetchFicha = async (id) => {
    try {
      const res = await api.get(`/marketing/prospectos/${id}`);
      setFicha(res.data);
    } catch (e) { toast.error('Error cargando ficha'); }
  };
  useEffect(() => {
    if (showFichaId) fetchFicha(showFichaId);
    else setFicha(null);
  }, [showFichaId]);

  const fetchWebhook = async () => {
    if (webhookInfo) { setShowWebhookInfo(true); return; }
    try {
      const res = await api.get('/marketing/webhook-info');
      setWebhookInfo(res.data);
      setShowWebhookInfo(true);
    } catch (e) { toast.error('Error'); }
  };

  const [syncing, setSyncing] = useState(false);
  const handleSyncMeta = async () => {
    if (!window.confirm('Sincronizar leads desde Meta Ads ahora? Trae todos los leads recientes de tus formularios (omitiendo los que ya existen).')) return;
    setSyncing(true);
    try {
      const res = await api.post('/marketing/meta/sync-leads');
      const { importados, duplicados_omitidos, formularios_revisados } = res.data;
      toast.success(`Sincronización OK: ${importados} nuevos · ${duplicados_omitidos} duplicados · ${formularios_revisados} forms`, { duration: 6000 });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const [syncingCamp, setSyncingCamp] = useState(false);
  const handleSyncMetaCampanas = async () => {
    if (!window.confirm('Sincronizar campañas y gasto real desde Meta Ads? Trae todas las campañas y sus métricas lifetime en CLP.')) return;
    setSyncingCamp(true);
    try {
      const res = await api.post('/marketing/meta/sync-campanas');
      toast.success(`Campañas: ${res.data.campanas_creadas} nuevas · ${res.data.campanas_actualizadas} actualizadas`, { duration: 5000 });
      // Vincular leads
      try {
        const v = await api.post('/marketing/meta/vincular-leads-campanas');
        if (v.data.leads_actualizados > 0) {
          toast.info(`${v.data.leads_actualizados} leads vinculados a sus campañas`);
        }
      } catch (e) { /* silent */ }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error sincronizando campañas');
    } finally {
      setSyncingCamp(false);
    }
  };

  // Filtros por tab/etapa
  const etapaActiva = tab === 'por_contactar' ? 'Por Contactar'
    : tab === 'calificados' ? 'Calificado'
    : tab === 'reunion' ? 'Reunión Socios'
    : tab === 'aceptados' ? 'Aceptado'
    : tab === 'perdidos' ? 'Perdido' : null;

  const prospectosFiltrados = prospectos.filter(p => {
    if (etapaActiva && p.etapa !== etapaActiva) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.nombre || '').toLowerCase().includes(q) ||
             (p.empresa || '').toLowerCase().includes(q) ||
             (p.email || '').toLowerCase().includes(q) ||
             (p.telefono || '').toLowerCase().includes(q) ||
             (p.rut || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="marketing-page">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Megaphone className="w-10 h-10 text-rose-500" strokeWidth={1.5} />
                <h1 className="text-4xl font-bold text-slate-900">Marketing & Leads</h1>
              </div>
              <p className="text-base text-slate-600">
                Captura inteligente desde Meta/Google, calificación con IA y atribución de ROI por campaña.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={handleSyncMeta} disabled={syncing} data-testid="sync-meta-btn" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                {syncing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sincronizando...</> : <><RefreshCcw className="w-4 h-4 mr-1" /> Sync Leads Meta</>}
              </Button>
              <Button variant="outline" size="sm" onClick={handleSyncMetaCampanas} disabled={syncingCamp} data-testid="sync-meta-camp-btn" className="border-purple-200 text-purple-700 hover:bg-purple-50">
                {syncingCamp ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sincronizando...</> : <><Activity className="w-4 h-4 mr-1" /> Sync Campañas Meta</>}
              </Button>
              <Button variant="outline" size="sm" onClick={fetchWebhook} data-testid="webhook-info-btn">
                <Link2 className="w-4 h-4 mr-1" /> URL Webhook
              </Button>
              <NuevoProspectoButton onCreated={fetchAll} />
            </div>
          </div>

          {/* Periodo selector + KPIs */}
          {dashboard && (
            <>
              <div className="flex items-center justify-end mb-2 gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-xs text-slate-600">Periodo:</span>
                <Select value={periodo} onValueChange={setPeriodo}>
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid="marketing-periodo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Últimos 7 días</SelectItem>
                    <SelectItem value="30d">Últimos 30 días</SelectItem>
                    <SelectItem value="90d">Últimos 90 días</SelectItem>
                    <SelectItem value="year">Último año</SelectItem>
                    <SelectItem value="all">Todo el tiempo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="marketing-kpis">
                <KpiCard icon={Users} label="Prospectos" value={dashboard.total_prospectos} color="slate" />
                <KpiCard icon={AlertTriangle} label="Urgentes" value={dashboard.urgencia_critica + (dashboard.precal_vencidos || 0) + (dashboard.sin_respuesta_vencidos || 0)} color="red" highlight={(dashboard.urgencia_critica + (dashboard.precal_vencidos || 0) + (dashboard.sin_respuesta_vencidos || 0)) > 0} />
                <KpiCard icon={ClipboardCheck} label="En Pre Cal" value={dashboard.en_precalificacion || 0} color="violet" highlight={(dashboard.en_precalificacion || 0) > 0} />
                <KpiCard icon={CheckCircle2} label="Aceptados" value={dashboard.aceptados} color="emerald" />
                <KpiCard icon={Sparkles} label="Recuperados" value={dashboard.recuperados || 0} color="teal" />
                <KpiCard icon={Target} label="CPL prom." value={fmtCLPCorto(dashboard.cpl_promedio_clp)} color="purple" />
                <KpiCard icon={TrendingUp} label="ROI global" value={`${dashboard.roi_global_x}x`} color={dashboard.roi_global_x >= 1 ? 'emerald' : 'rose'} />
              </div>
            </>
          )}

          {/* Tabs principales */}
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 mb-5 gap-1 h-auto p-1" data-testid="marketing-tabs">
              <TabsTrigger value="dashboard" className="text-xs px-2 py-2 data-[state=active]:bg-rose-500 data-[state=active]:text-white">
                <BarChart3 className="w-3.5 h-3.5 mr-1" />Dashboard
              </TabsTrigger>
              <TabsTrigger value="pipeline" className="text-xs px-2 py-2 data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-pipeline">
                <BellRing className="w-3.5 h-3.5 mr-1" />Pipeline
                {dashboard?.por_contactar > 0 && <Badge className="ml-1 bg-white text-amber-700 text-[10px] h-4 px-1">{dashboard.por_contactar}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="cerebro_ia" className="text-xs px-2 py-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-testid="tab-cerebro-ia">
                <Sparkles className="w-3.5 h-3.5 mr-1" />Cerebro IA
              </TabsTrigger>
              <TabsTrigger value="campanas" className="text-xs px-2 py-2 data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                <Activity className="w-3.5 h-3.5 mr-1" />Campañas
              </TabsTrigger>
              <TabsTrigger value="embudo" className="text-xs px-2 py-2 data-[state=active]:bg-rose-500 data-[state=active]:text-white">
                <Target className="w-3.5 h-3.5 mr-1" />Embudo
              </TabsTrigger>
              <TabsTrigger value="historial" className="text-xs px-2 py-2 data-[state=active]:bg-slate-700 data-[state=active]:text-white" data-testid="tab-historial">
                <HistoryIcon className="w-3.5 h-3.5 mr-1" />Historial
              </TabsTrigger>
            </TabsList>

            {/* DASHBOARD */}
            <TabsContent value="dashboard">
              <DashboardTab dashboard={dashboard} embudo={embudo} campanas={campanas} />
            </TabsContent>

            {/* PIPELINE KANBAN (state machine estricto) */}
            <TabsContent value="pipeline">
              <KanbanLeadsPipeline onLeadClick={(id) => setShowFichaId(id)} />
            </TabsContent>

            {/* CEREBRO IA */}
            <TabsContent value="cerebro_ia">
              <CerebroIA />
            </TabsContent>

            {/* CAMPAÑAS */}
            <TabsContent value="campanas">
              <CampanasTab campanas={campanas} onChange={fetchAll} />
            </TabsContent>

            {/* EMBUDO */}
            <TabsContent value="embudo">
              <EmbudoTab embudo={embudo} />
            </TabsContent>

            {/* HISTORIAL */}
            <TabsContent value="historial">
              <HistorialLeads onLeadClick={(id) => setShowFichaId(id)} />
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* FICHA PROSPECTO */}
      <Dialog open={!!showFichaId} onOpenChange={(v) => !v && setShowFichaId(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ficha-prospecto-dialog">
          {ficha ? (
            <FichaProspecto
              ficha={ficha}
              campanas={campanas}
              onClose={() => setShowFichaId(null)}
              onChange={() => { fetchAll(); fetchFicha(ficha.id); }}
            />
          ) : (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-rose-400 mx-auto" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* WEBHOOK INFO */}
      <Dialog open={showWebhookInfo} onOpenChange={setShowWebhookInfo}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="webhook-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-rose-500" /> Conectar Meta Lead Ads y Google Lead Forms
            </DialogTitle>
            <DialogDescription>
              Sigue los pasos por plataforma. Cada lead llegará en tiempo real con beep + toast.
            </DialogDescription>
          </DialogHeader>
          {webhookInfo && (
            <WebhookSetupGuide info={webhookInfo} backendBase={(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== GUÍA WEBHOOK ====================

const CopyableField = ({ label, value, mono = true }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="flex gap-2">
      <Input readOnly value={value} className={mono ? 'font-mono text-xs' : 'text-xs'} />
      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(value); toast.success('Copiado'); }}>
        <Copy className="w-3.5 h-3.5" />
      </Button>
    </div>
  </div>
);

const WebhookSetupGuide = ({ info, backendBase }) => {
  const [tab, setTab] = useState('meta');
  const fullUrl = `${backendBase}${info.endpoint}`;
  const metaVerify = info.meta?.verify_token || '';
  const ejGoogle = (info.ejemplos?.google_simulado || '').replace('<BASE_URL>', backendBase);
  const ejGen = (info.ejemplos?.generico || '').replace('<BASE_URL>', backendBase);

  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full">
      <TabsList className="grid grid-cols-3 mb-3">
        <TabsTrigger value="meta" data-testid="guide-tab-meta">Meta Lead Ads</TabsTrigger>
        <TabsTrigger value="google" data-testid="guide-tab-google">Google Lead Forms</TabsTrigger>
        <TabsTrigger value="generico" data-testid="guide-tab-generico">Genérico / Zapier</TabsTrigger>
      </TabsList>

      {/* ===== META ===== */}
      <TabsContent value="meta" className="space-y-3">
        <Card className="p-3 bg-blue-50 border-blue-200 text-xs space-y-1">
          <p className="font-semibold text-blue-900">Resumen rápido</p>
          <p className="text-blue-800">
            Meta solo te manda <strong>el ID del lead</strong>, no los datos. El sistema usa el <strong>Page Access Token</strong> que pegues en backend/.env para traerlos automáticamente con la Graph API.
          </p>
        </Card>

        <CopyableField label="1️⃣ Callback URL (pégalo en Meta App → Webhooks)" value={fullUrl} />
        <CopyableField label="2️⃣ Verify Token (pégalo cuando suscribas el webhook)" value={metaVerify} />

        <Card className="p-3 border-slate-200 space-y-2 text-xs">
          <p className="font-semibold text-slate-800">Pasos en Meta for Developers</p>
          <ol className="list-decimal pl-4 space-y-1 text-slate-700">
            <li>Ve a <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-rose-600 underline">developers.facebook.com/apps</a> → crea/abre tu app de tipo <strong>Business</strong>.</li>
            <li>Agrega el producto <strong>Webhooks</strong>. Elige objeto <strong>Page</strong>.</li>
            <li>Pega la <strong>Callback URL</strong> y el <strong>Verify Token</strong> de arriba. Meta hará GET de verificación → debe devolver 200.</li>
            <li>Suscribe el campo <strong>leadgen</strong>.</li>
            <li>Agrega producto <strong>Lead Ads (Marketing API)</strong> y suscribe tu Página al webhook.</li>
            <li>En <strong>Graph API Explorer</strong>: selecciona tu Página y solicita los permisos <code className="font-mono bg-slate-100 px-1">leads_retrieval</code>, <code className="font-mono bg-slate-100 px-1">pages_manage_metadata</code>, <code className="font-mono bg-slate-100 px-1">pages_show_list</code>. Genera un <strong>Page Access Token de larga duración</strong>.</li>
            <li>Pega ese token en <code className="font-mono bg-slate-100 px-1">backend/.env</code> como <code className="font-mono bg-slate-100 px-1">META_PAGE_ACCESS_TOKEN=...</code> y reinicia el backend.</li>
            <li>Opcional pero recomendado: copia el <strong>App Secret</strong> a <code className="font-mono bg-slate-100 px-1">META_APP_SECRET=...</code> para validar la firma X-Hub-Signature-256.</li>
          </ol>
          <p className="text-slate-700">
            <strong>Estado actual:</strong>{' '}
            <Badge className={info.meta?.page_access_token_configurado ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              Page Access Token: {info.meta?.page_access_token_configurado ? 'OK' : 'PENDIENTE'}
            </Badge>{' '}
            <Badge className={info.meta?.app_secret_configurado ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
              App Secret: {info.meta?.app_secret_configurado ? 'OK' : 'opcional'}
            </Badge>
          </p>
        </Card>

        <Card className="p-3 border-slate-200 space-y-1 text-xs">
          <p className="font-semibold text-slate-800">Probar antes de pagar campañas</p>
          <p className="text-slate-700">
            En <strong>Meta Lead Ads Testing Tool</strong>: <a href="https://developers.facebook.com/tools/lead-ads-testing" target="_blank" rel="noopener noreferrer" className="text-rose-600 underline">developers.facebook.com/tools/lead-ads-testing</a> selecciona tu Página y formulario, y dispara un lead de prueba. Verás aparecer el prospecto aquí en menos de 5 segundos.
          </p>
        </Card>
      </TabsContent>

      {/* ===== GOOGLE ===== */}
      <TabsContent value="google" className="space-y-3">
        <Card className="p-3 bg-blue-50 border-blue-200 text-xs">
          <p className="font-semibold text-blue-900 mb-1">Resumen rápido</p>
          <p className="text-blue-800">
            Google envía <strong>los datos completos</strong> del lead directamente en cada POST. Configura los campos del formulario y pega esta URL en tu campaña.
          </p>
        </Card>

        <CopyableField label="1️⃣ Webhook URL (pégalo en Google Ads → Lead Form Extensions)" value={fullUrl} />
        <CopyableField label="2️⃣ Key alternativo (también puedes usar X-Marketing-Secret header)" value={info.secret_token} />

        <Card className="p-3 border-slate-200 space-y-2 text-xs">
          <p className="font-semibold text-slate-800">Pasos en Google Ads</p>
          <ol className="list-decimal pl-4 space-y-1 text-slate-700">
            <li>Abre tu campaña → <strong>Anuncios y recursos → Recursos → Formulario para clientes potenciales</strong>.</li>
            <li>Crea o edita un formulario. En la sección <strong>Lead delivery → Webhook integration</strong>:</li>
            <li>Pega la <strong>Webhook URL</strong> de arriba.</li>
            <li>En <strong>Key</strong> pega: <code className="font-mono bg-slate-100 px-1">{info.secret_token}</code> (tu MARKETING_WEBHOOK_SECRET).</li>
            <li>Configura los campos del formulario usando los nombres estándar de Google: <code className="font-mono bg-slate-100 px-1">FULL_NAME, EMAIL, PHONE_NUMBER, COMPANY_NAME</code>.</li>
            <li>Click <strong>Send test data</strong> → debe responder 200. El lead test aparecerá aquí.</li>
            <li>(Opcional para producción) Pega tu propio secret en <code className="font-mono bg-slate-100 px-1">backend/.env</code> como <code className="font-mono bg-slate-100 px-1">GOOGLE_WEBHOOK_SECRET=...</code> y configúralo también en Google para validación HMAC.</li>
          </ol>
        </Card>

        <Card className="p-3 border-slate-200 text-xs space-y-1">
          <p className="font-semibold text-slate-800">Probar manualmente con cURL</p>
          <Textarea readOnly value={ejGoogle} className="font-mono text-[11px] h-28" />
        </Card>
      </TabsContent>

      {/* ===== GENÉRICO ===== */}
      <TabsContent value="generico" className="space-y-3">
        <Card className="p-3 bg-blue-50 border-blue-200 text-xs">
          <p className="text-blue-800">
            Usa este modo para tu propia web, formularios Tally / Typeform / Zapier / Make, o para mandar leads manualmente.
          </p>
        </Card>

        <CopyableField label="Endpoint completo" value={fullUrl} />
        <CopyableField label="Header de autenticación" value={info.header_auth} />
        <CopyableField label="Token secreto" value={info.secret_token} />

        <Card className="p-3 border-slate-200 text-xs space-y-1">
          <p className="font-semibold text-slate-800">Ejemplo cURL</p>
          <Textarea readOnly value={ejGen} className="font-mono text-[11px] h-28" />
        </Card>

        <Card className="p-3 bg-amber-50 border-amber-200 text-xs">
          <p className="text-amber-800">
            💡 Cambia <code className="font-mono">MARKETING_WEBHOOK_SECRET</code> en producción y rota el token periódicamente.
          </p>
        </Card>
      </TabsContent>
    </Tabs>
  );
};


// ==================== SUBCOMPONENTES ====================

const KpiCard = ({ icon: Icon, label, value, color, highlight }) => {
  const colors = {
    slate: 'border-slate-200 bg-white',
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50',
    emerald: 'border-emerald-200 bg-emerald-50',
    indigo: 'border-indigo-200 bg-indigo-50',
    purple: 'border-purple-200 bg-purple-50',
    rose: 'border-rose-200 bg-rose-50',
    violet: 'border-violet-200 bg-violet-50',
    teal: 'border-teal-200 bg-teal-50'
  };
  const txt = {
    slate: 'text-slate-700', amber: 'text-amber-700', red: 'text-red-700',
    emerald: 'text-emerald-700', indigo: 'text-indigo-700', purple: 'text-purple-700', rose: 'text-rose-700',
    violet: 'text-violet-700', teal: 'text-teal-700'
  };
  return (
    <Card className={`p-3 ${colors[color] || colors.slate} ${highlight ? 'ring-2 ring-offset-1 ring-amber-300 animate-pulse' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${txt[color] || txt.slate}`} />
        <span className={`text-[10px] uppercase tracking-wide font-semibold ${txt[color] || txt.slate}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${txt[color] || txt.slate}`}>{value}</p>
    </Card>
  );
};

const ProspectoRow = ({ prospecto, onClick }) => {
  const c = ETAPA_COLOR[prospecto.etapa] || ETAPA_COLOR['Por Contactar'];
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-slate-200 hover:border-rose-300 hover:shadow-sm rounded-lg p-3 transition-all flex items-center gap-3"
      data-testid={`prospecto-row-${prospecto.id}`}
    >
      <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-slate-900 truncate">{prospecto.nombre}</p>
          {prospecto.urgencia_critica && (
            <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px]">
              <AlertTriangle className="w-3 h-3 mr-0.5" /> URGENCIA CRÍTICA
            </Badge>
          )}
          {prospecto.score && (
            <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 text-[10px]">
              <Star className="w-3 h-3 mr-0.5 fill-amber-400" /> {prospecto.score}/10
            </Badge>
          )}
          {prospecto.score_ia_sugerido && !prospecto.score && (
            <Badge variant="outline" className="border-purple-200 text-purple-700 bg-purple-50 text-[10px]">
              <Sparkles className="w-3 h-3 mr-0.5" /> IA: {prospecto.score_ia_sugerido}/10
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 truncate">
          {prospecto.empresa || '—'} · {prospecto.email || prospecto.telefono || 'sin contacto'} · {prospecto.origen}
          {prospecto.campana_nombre && ` · ${prospecto.campana_nombre}`}
        </p>
      </div>
      <Badge variant="outline" className={`text-[10px] ${c.bg} ${c.text} ${c.border}`}>{prospecto.etapa}</Badge>
      <span className="text-[10px] text-slate-400 hidden md:inline">
        {new Date(prospecto.created_at).toLocaleDateString('es-CL')}
      </span>
      <ChevronRight className="w-4 h-4 text-slate-300" />
    </button>
  );
};

// ==================== FICHA PROSPECTO ====================

const EditableField = ({ label, icon: Icon, value, onChange, onBlur, saving, saved, multiline, type = 'text', testId }) => {
  const handleKeyDown = (e) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };
  const InputCmp = multiline ? Textarea : Input;
  return (
    <div className="bg-slate-50 p-2 rounded border border-slate-200 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">
          {Icon && <Icon className="w-3 h-3" />} {label}
        </p>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-indigo-500" />}
        {saved && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
      </div>
      <InputCmp
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        rows={multiline ? 3 : undefined}
        data-testid={testId}
        className="text-sm bg-transparent border-none px-0 py-0 h-auto min-h-0 focus-visible:ring-0 shadow-none"
        placeholder={`Agregar ${label.toLowerCase()}...`}
      />
    </div>
  );
};

const FichaProspecto = ({ ficha, campanas, onClose, onChange }) => {
  const [interaccionForm, setInteraccionForm] = useState({ tipo: 'llamada', descripcion: '', duracion_min: '' });
  const [showPrecalModal, setShowPrecalModal] = useState(false);
  const [editForm, setEditForm] = useState({
    nombre: ficha.nombre || '',
    email: ficha.email || '',
    telefono: ficha.telefono || '',
    mensaje: ficha.mensaje || '',
  });
  const [savingField, setSavingField] = useState(null);
  const [savedField, setSavedField] = useState(null);
  const c = ETAPA_COLOR[ficha.etapa] || ETAPA_COLOR['Por Contactar'];

  // Sincronizar form al cambiar de ficha
  useEffect(() => {
    setEditForm({
      nombre: ficha.nombre || '',
      email: ficha.email || '',
      telefono: ficha.telefono || '',
      mensaje: ficha.mensaje || '',
    });
  }, [ficha.id]);

  const saveField = async (field) => {
    const value = (editForm[field] || '').trim();
    const original = (ficha[field] || '').trim();
    if (value === original) return;
    setSavingField(field);
    try {
      await api.put(`/marketing/prospectos/${ficha.id}`, { [field]: value });
      setSavedField(field);
      setTimeout(() => setSavedField(null), 1500);
      onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : `Error guardando ${field}`);
      setEditForm(prev => ({ ...prev, [field]: ficha[field] || '' }));
    } finally {
      setSavingField(null);
    }
  };

  const handleAddInteraccion = async () => {
    if (interaccionForm.descripcion.trim().length < 3) {
      toast.error('Describe la interacción (mín. 3 caracteres)');
      return;
    }
    try {
      const res = await api.post(`/marketing/prospectos/${ficha.id}/interacciones`, {
        tipo: interaccionForm.tipo,
        descripcion: interaccionForm.descripcion.trim(),
        duracion_min: interaccionForm.duracion_min ? parseInt(interaccionForm.duracion_min) : null
      });
      if (res.data.urgencia_detectada) {
        toast.warning('⚠️ Urgencia crítica detectada en la interacción');
      } else {
        toast.success('Interacción registrada');
      }
      setInteraccionForm({ tipo: 'llamada', descripcion: '', duracion_min: '' });
      onChange();
    } catch (e) {
      toast.error('Error');
    }
  };

  const handleUploadDoc = (tipo) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      try {
        await api.post(`/marketing/prospectos/${ficha.id}/documentos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Documento subido');
        onChange();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error');
      }
    };
    input.click();
  };

  const handleVerDoc = (docId) => {
    api.get(`/marketing/prospectos/${ficha.id}/documentos/${docId}`, { responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(new Blob([res.data]));
        window.open(url, '_blank');
      })
      .catch(() => toast.error('Error abriendo documento'));
  };

  const handleEliminarDoc = async (docId) => {
    if (!window.confirm('¿Eliminar documento?')) return;
    try {
      await api.delete(`/marketing/prospectos/${ficha.id}/documentos/${docId}`);
      toast.success('Eliminado');
      onChange();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div data-testid="ficha-prospecto-content">
      <DialogHeader>
        <div className="flex items-start justify-between pr-8 gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-2xl flex items-center gap-2 flex-wrap">
              {ficha.nombre}
              {ficha.urgencia_critica && (
                <Badge className="bg-red-600 text-white text-xs">
                  <AlertTriangle className="w-3 h-3 mr-0.5" /> URGENCIA CRÍTICA
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="space-y-0.5">
              <span className="block">{ficha.empresa || 'Sin empresa'} · {ficha.rut || 'sin RUT'}</span>
              <span className="block text-xs">
                {ficha.origen}{ficha.campana_nombre ? ` · ${ficha.campana_nombre}` : ''} · creado {new Date(ficha.created_at).toLocaleDateString('es-CL')}
              </span>
            </DialogDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`${c.bg} ${c.text} ${c.border}`}>{ficha.etapa}</Badge>
          </div>
        </div>
      </DialogHeader>

      {/* Urgencia keywords */}
      {ficha.urgencia_critica && (ficha.urgencia_keywords || []).length > 0 && (
        <Card className="p-3 my-3 bg-red-50 border-red-200">
          <p className="text-xs text-red-800">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            Palabras clave detectadas: <strong>{ficha.urgencia_keywords.join(', ')}</strong>
          </p>
        </Card>
      )}

      {/* Datos básicos editables */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
        <EditableField
          label="Nombre"
          icon={UserIcon}
          value={editForm.nombre}
          onChange={(v) => setEditForm(prev => ({ ...prev, nombre: v }))}
          onBlur={() => saveField('nombre')}
          saving={savingField === 'nombre'}
          saved={savedField === 'nombre'}
          testId="ficha-edit-nombre"
        />
        <EditableField
          label="Email"
          icon={Mail}
          type="email"
          value={editForm.email}
          onChange={(v) => setEditForm(prev => ({ ...prev, email: v }))}
          onBlur={() => saveField('email')}
          saving={savingField === 'email'}
          saved={savedField === 'email'}
          testId="ficha-edit-email"
        />
        <EditableField
          label="Teléfono"
          icon={Phone}
          value={editForm.telefono}
          onChange={(v) => setEditForm(prev => ({ ...prev, telefono: v }))}
          onBlur={() => saveField('telefono')}
          saving={savingField === 'telefono'}
          saved={savedField === 'telefono'}
          testId="ficha-edit-telefono"
        />
        <div className="md:col-span-3">
          <EditableField
            label="Mensaje inicial"
            icon={MessageSquare}
            multiline
            value={editForm.mensaje}
            onChange={(v) => setEditForm(prev => ({ ...prev, mensaje: v }))}
            onBlur={() => saveField('mensaje')}
            saving={savingField === 'mensaje'}
            saved={savedField === 'mensaje'}
            testId="ficha-edit-mensaje"
          />
        </div>
      </div>

      <Tabs defaultValue="scoring" className="w-full">
        <TabsList className="grid grid-cols-4 mb-3">
          <TabsTrigger value="scoring" data-testid="ficha-tab-scoring"><ClipboardCheck className="w-3.5 h-3.5 mr-1" />Pre Calificación</TabsTrigger>
          <TabsTrigger value="credenciales" data-testid="ficha-tab-credenciales"><Star className="w-3.5 h-3.5 mr-1" />Credenciales ({(ficha.credenciales || []).length})</TabsTrigger>
          <TabsTrigger value="interacciones" data-testid="ficha-tab-interacciones"><MessageSquare className="w-3.5 h-3.5 mr-1" />Interacciones ({(ficha.interacciones || []).length})</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="ficha-tab-documentos"><FileText className="w-3.5 h-3.5 mr-1" />Documentos ({(ficha.documentos || []).length})</TabsTrigger>
        </TabsList>

        {/* SCORING */}
        <TabsContent value="scoring" className="space-y-4">
          <PreCalSection ficha={ficha} onOpenPrecal={() => setShowPrecalModal(true)} />
        </TabsContent>

        {/* CREDENCIALES */}
        <TabsContent value="credenciales" className="space-y-3">
          <CredencialesSection ficha={ficha} onChange={onChange} />
        </TabsContent>

        {/* INTERACCIONES */}
        <TabsContent value="interacciones" className="space-y-3">
          <Card className="p-4 border-slate-200">
            <h4 className="font-semibold text-slate-800 mb-2 text-sm">Nueva interacción</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
              <Select value={interaccionForm.tipo} onValueChange={(v) => setInteraccionForm({...interaccionForm, tipo: v})}>
                <SelectTrigger data-testid="interaccion-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="llamada">Llamada</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="reunion">Reunión</SelectItem>
                  <SelectItem value="nota">Nota interna</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Duración (min)"
                value={interaccionForm.duracion_min}
                onChange={(e) => setInteraccionForm({...interaccionForm, duracion_min: e.target.value})}
              />
              <Button onClick={handleAddInteraccion} className="bg-rose-500 hover:bg-rose-600" data-testid="add-interaccion-btn">
                <Plus className="w-4 h-4 mr-1" /> Registrar
              </Button>
            </div>
            <Textarea
              rows={3}
              placeholder="Describe el contacto, mensaje del cliente, palabras textuales (la IA leerá esto)…"
              value={interaccionForm.descripcion}
              onChange={(e) => setInteraccionForm({...interaccionForm, descripcion: e.target.value})}
              data-testid="interaccion-desc-input"
            />
          </Card>

          {(ficha.interacciones || []).slice().reverse().map(i => (
            <Card key={i.id} className="p-3 border-slate-200" data-testid={`interaccion-${i.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px] capitalize border-rose-200 text-rose-700 bg-rose-50">
                  {i.tipo}
                </Badge>
                {i.duracion_min && <span className="text-[11px] text-slate-500">{i.duracion_min} min</span>}
                <span className="text-[11px] text-slate-400 ml-auto">
                  {new Date(i.fecha).toLocaleString('es-CL')} · {i.creado_por}
                </span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-line">{i.descripcion}</p>
            </Card>
          ))}
        </TabsContent>

        {/* DOCUMENTOS */}
        <TabsContent value="documentos" className="space-y-3">
          <Card className="p-4 border-slate-200">
            <h4 className="font-semibold text-slate-800 mb-2 text-sm">Subir antecedente</h4>
            <p className="text-xs text-slate-500 mb-2">PDF o imagen (PNG/JPG/WEBP). Máx. 15 MB.</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'carpeta_tributaria', l: 'Carpeta Tributaria' },
                { v: 'deudas_tgr', l: 'Deudas TGR' },
                { v: 'otro', l: 'Otro' }
              ].map(t => (
                <Button
                  key={t.v}
                  variant="outline"
                  size="sm"
                  className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                  onClick={() => handleUploadDoc(t.v)}
                  data-testid={`upload-${t.v}-btn`}
                >
                  <Upload className="w-3.5 h-3.5 mr-1" /> {t.l}
                </Button>
              ))}
            </div>
          </Card>

          {(ficha.documentos || []).length === 0 ? (
            <Card className="p-8 text-center border-dashed border-slate-300">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Sin documentos cargados todavía</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(ficha.documentos || []).map(d => (
                <Card key={d.id} className="p-3 border-slate-200 flex items-center gap-2" data-testid={`doc-${d.id}`}>
                  {d.ext === '.pdf' ? <FileText className="w-5 h-5 text-rose-500" /> : <ImageIcon className="w-5 h-5 text-blue-500" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.filename}</p>
                    <p className="text-[11px] text-slate-500">
                      {d.tipo.replace(/_/g, ' ')} · {(d.size_bytes / 1024).toFixed(1)} KB · {new Date(d.uploaded_at).toLocaleDateString('es-CL')}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleVerDoc(d.id)} data-testid={`view-doc-${d.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleEliminarDoc(d.id)} data-testid={`del-doc-${d.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Pre Calificación */}
      {showPrecalModal && (
        <PreCalModal
          lead={ficha}
          onClose={() => setShowPrecalModal(false)}
          onSuccess={() => { setShowPrecalModal(false); onChange(); }}
        />
      )}
    </div>
  );
};

/* ===================== PRE CAL SECTION (dentro de la ficha) ===================== */
const PRECAL_LABELS = {
  recomendacion_socio: 'Recomendación de socio',
  decisor_directo: 'Decisor directo o con poder de firma',
  recomendacion_precalificador: 'Recomendación del precalificador',
  facturacion_5mm: 'Facturación mensual > $5 MM CLP',
  urgencia_cliente: 'Urgencia manifestada por el cliente',
  relacion_servicios_peritum: 'Relación con servicios Peritum',
  disposicion_firma_cps: 'Disposición a firmar CPS en <30 días',
  mas_de_un_servicio: 'Requiere más de un servicio',
  antiguedad_2_anos: 'Empresa con >2 años de operación',
  sector_estrategico: 'Sector económico estratégico',
  sin_competidor_instalado: 'Sin abogado/contador externo actual',
  contacto_efectivo: 'Contacto efectivo (respondió en <24h)',
};
const PRECAL_ETAPAS_PERMITEN = new Set(['Contactado', 'Sin Respuesta']);

const PreCalSection = ({ ficha, onOpenPrecal }) => {
  const puedeIniciar = PRECAL_ETAPAS_PERMITEN.has(ficha.etapa);
  const yaTieneScore = ficha.precal_score_pct != null;
  const score = ficha.precal_score_pct;
  const aprobado = score != null && score >= 50;
  const historial = ficha.precal_historial || [];

  return (
    <div className="space-y-3">
      <Card className="p-4 border-violet-200 bg-violet-50/40">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-800 flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-violet-600" /> Pre Calificación
            </h4>
            <p className="text-xs text-slate-600 mt-1">
              Checklist de 12 ítems ponderados (umbral 50%). Solo disponible desde <strong>Contactado</strong> o <strong>Sin Respuesta</strong>.
            </p>
          </div>
          {puedeIniciar ? (
            <Button onClick={onOpenPrecal} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ficha-iniciar-precal">
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> {yaTieneScore ? 'Repetir Pre Calificación' : 'Iniciar Pre Calificación'}
            </Button>
          ) : (
            <Badge variant="outline" className="bg-slate-100">No disponible en etapa "{ficha.etapa}"</Badge>
          )}
        </div>
      </Card>

      {/* Último resultado */}
      {yaTieneScore && (
        <Card className={`p-4 border-2 ${aprobado ? 'border-emerald-300 bg-emerald-50/40' : 'border-rose-300 bg-rose-50/30'}`}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Último score</p>
                <p className={`text-4xl font-bold leading-none ${aprobado ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {Number(score).toFixed(0)}<span className="text-2xl">%</span>
                </p>
              </div>
              <Badge className={aprobado ? 'bg-emerald-600 text-white' : 'bg-rose-500 text-white'}>
                {aprobado ? '≥ 50% · Puede calificar' : '< 50% · Queda en Precalificación'}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">
              {ficha.precal_updated_at && `Actualizado ${new Date(ficha.precal_updated_at).toLocaleString('es-CL')}`}
              {ficha.precal_updated_by && ` · ${ficha.precal_updated_by}`}
            </p>
          </div>
          {(ficha.precal_items_marcados || []).length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-slate-700 mb-1.5">Ítems marcados:</p>
              <div className="flex flex-wrap gap-1.5">
                {(ficha.precal_items_marcados || []).map(k => (
                  <Badge key={k} className="bg-white text-slate-700 border-slate-300 text-xs">
                    <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />
                    {PRECAL_LABELS[k] || k}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Historial */}
      {historial.length > 1 && (
        <Card className="p-3 border-slate-200">
          <p className="text-xs font-semibold text-slate-700 mb-2">Historial ({historial.length} pre-calificaciones)</p>
          <div className="space-y-1.5">
            {historial.slice().reverse().map(h => (
              <div key={h.id} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded border border-slate-100">
                <span className="text-slate-600">
                  {new Date(h.at).toLocaleString('es-CL')} · {h.by}
                </span>
                <Badge className={h.aprobado ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-rose-100 text-rose-700 border-rose-200'}>
                  {Number(h.score_pct).toFixed(0)}%
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!puedeIniciar && !yaTieneScore && (
        <Card className="p-3 border-slate-200 bg-slate-50 text-center">
          <p className="text-xs text-slate-500">
            Mueve el lead a "Contactado" o "Sin Respuesta" para habilitar la Pre Calificación.
          </p>
        </Card>
      )}
    </div>
  );
};

/* ===================== CREDENCIALES SECTION ===================== */
const ETAPAS_PERMITEN_CREDENCIALES = new Set([
  'Contactado', 'Sin Respuesta', 'En Precalificación',
  'Calificado', 'No Calificado', 'Reunión Socios',
  'Aceptado', 'Perdido', 'Descartado'
]);

const CredencialesSection = ({ ficha, onChange }) => {
  const puedeEditar = ETAPAS_PERMITEN_CREDENCIALES.has(ficha.etapa);
  const [creds, setCreds] = useState(
    (ficha.credenciales || []).map(c => ({ ...c, _showSii: false, _showUnica: false }))
  );
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    if (creds.length >= 3) return;
    setCreds([...creds, { label: '', rut: '', clave_sii: '', clave_unica: '', _showSii: true, _showUnica: true, _new: true }]);
  };

  const updateItem = (idx, patch) => {
    setCreds(creds.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };

  const removeItem = (idx) => {
    setCreds(creds.filter((_, i) => i !== idx));
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        credenciales: creds
          .filter(c => (c.rut || '').trim())
          .map(c => ({
            id: c.id,
            label: (c.label || '').trim(),
            rut: c.rut.trim(),
            clave_sii: c.clave_sii || '',
            clave_unica: c.clave_unica || '',
          }))
      };
      const r = await api.put(`/marketing/prospectos/${ficha.id}/credenciales`, payload);
      toast.success(`${r.data.total} credencial${r.data.total === 1 ? '' : 'es'} guardada${r.data.total === 1 ? '' : 's'}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando credenciales');
    } finally {
      setSaving(false);
    }
  };

  if (!puedeEditar) {
    return (
      <Card className="p-6 text-center border-amber-200 bg-amber-50/40">
        <Star className="w-10 h-10 text-amber-400 mx-auto mb-2" />
        <p className="text-sm text-slate-700 font-semibold">Credenciales no disponibles en esta etapa</p>
        <p className="text-xs text-slate-600 mt-1">Mueve el lead a <strong>"Contactado"</strong> para registrar RUT, Clave SII y Clave Única.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card className="p-4 border-violet-200 bg-violet-50/40">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h4 className="font-semibold text-slate-800 flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-violet-600" /> Credenciales del cliente
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Registra RUT + Clave SII + Clave Única. Máximo <strong>3 sets</strong> (ej. empresa principal, persona natural, otra empresa).
            </p>
          </div>
          {creds.length < 3 && (
            <Button size="sm" variant="outline" onClick={addItem} className="border-violet-300 text-violet-700 hover:bg-violet-100" data-testid="cred-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Agregar credencial
            </Button>
          )}
        </div>
      </Card>

      {creds.length === 0 && (
        <Card className="p-6 text-center border-dashed border-slate-300">
          <p className="text-sm text-slate-500">Sin credenciales registradas todavía.</p>
          <Button size="sm" onClick={addItem} className="mt-2 bg-violet-600 hover:bg-violet-700 text-white" data-testid="cred-add-first-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Agregar la primera
          </Button>
        </Card>
      )}

      {creds.map((c, idx) => (
        <Card key={c.id || `new-${idx}`} className="p-3 border-slate-200" data-testid={`cred-item-${idx}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-violet-100 text-violet-800 border-violet-200">Credencial #{idx + 1}</Badge>
              <Input
                placeholder="Etiqueta (ej: Empresa principal)"
                value={c.label || ''}
                onChange={(e) => updateItem(idx, { label: e.target.value })}
                className="h-7 text-xs w-56"
                data-testid={`cred-label-${idx}`}
              />
            </div>
            <Button size="sm" variant="ghost" className="text-rose-600 h-7" onClick={() => removeItem(idx)} data-testid={`cred-remove-${idx}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">RUT *</Label>
              <Input
                placeholder="76123456-7"
                value={c.rut || ''}
                onChange={(e) => updateItem(idx, { rut: e.target.value })}
                data-testid={`cred-rut-${idx}`}
              />
            </div>
            <div>
              <Label className="text-xs">Clave SII</Label>
              <div className="relative">
                <Input
                  type={c._showSii ? 'text' : 'password'}
                  placeholder="••••••"
                  value={c.clave_sii || ''}
                  onChange={(e) => updateItem(idx, { clave_sii: e.target.value })}
                  className="pr-9"
                  data-testid={`cred-sii-${idx}`}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  onClick={() => updateItem(idx, { _showSii: !c._showSii })}
                  title={c._showSii ? 'Ocultar' : 'Mostrar'}
                  data-testid={`cred-sii-toggle-${idx}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Clave Única</Label>
              <div className="relative">
                <Input
                  type={c._showUnica ? 'text' : 'password'}
                  placeholder="••••••"
                  value={c.clave_unica || ''}
                  onChange={(e) => updateItem(idx, { clave_unica: e.target.value })}
                  className="pr-9"
                  data-testid={`cred-unica-${idx}`}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  onClick={() => updateItem(idx, { _showUnica: !c._showUnica })}
                  title={c._showUnica ? 'Ocultar' : 'Mostrar'}
                  data-testid={`cred-unica-toggle-${idx}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </Card>
      ))}

      {creds.length > 0 && (
        <div className="flex justify-end">
          <Button onClick={guardar} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="cred-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : <><Star className="w-4 h-4 mr-2" /> Guardar credenciales</>}
          </Button>
        </div>
      )}
    </div>
  );
};

// ==================== DASHBOARD TAB ====================

const DashboardTab = ({ dashboard, embudo, campanas }) => {
  if (!dashboard) return null;
  return (
    <div className="space-y-4">
      {/* Sección BI principal: embudo+insights, demografía Meta, heatmap */}
      <BIAnalytics />

      {/* Performance por campaña (existente) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-5 lg:col-span-2 border-slate-200">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-rose-500" /> Performance por campaña
        </h3>
        {campanas.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Aún no hay campañas registradas.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-200">
                <th className="py-2">Campaña</th>
                <th className="py-2">Plataforma</th>
                <th className="py-2 text-right">Inversión</th>
                <th className="py-2 text-right">Leads</th>
                <th className="py-2 text-right">CPL</th>
                <th className="py-2 text-right">CTR</th>
                <th className="py-2 text-right">Conv. UF</th>
                <th className="py-2 text-right">ROI</th>
              </tr>
            </thead>
            <tbody>
              {campanas.map(c => (
                <tr key={c.id} className="border-b border-slate-100" data-testid={`campana-row-${c.id}`}>
                  <td className="py-2 font-medium text-slate-800">{c.nombre}</td>
                  <td className="py-2"><Badge variant="outline" className="text-[10px]">{c.plataforma}</Badge></td>
                  <td className="py-2 text-right tabular-nums">{c.inversion_uf} UF</td>
                  <td className="py-2 text-right tabular-nums">{c.metricas.leads_total}</td>
                  <td className="py-2 text-right tabular-nums">{c.metricas.cpl_uf} UF</td>
                  <td className="py-2 text-right tabular-nums">{c.metricas.ctr}%</td>
                  <td className="py-2 text-right tabular-nums font-semibold text-emerald-700">{c.metricas.conversion_uf} UF</td>
                  <td className={`py-2 text-right font-bold ${c.metricas.roi_x >= 1 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {c.metricas.roi_x}x
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-5 border-slate-200">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-rose-500" /> Embudo
        </h3>
        {embudo && (
          <div className="space-y-2">
            {ETAPAS.map((e, i) => {
              const cant = embudo.por_etapa[e] || 0;
              const total = embudo.total_leads || 1;
              const pct = Math.round((cant / total) * 100);
              const col = ETAPA_COLOR[e];
              return (
                <div key={e}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="text-slate-700 font-medium">{e}</span>
                    <span className="text-slate-500 tabular-nums">{cant} · {pct}%</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full ${col.dot}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="border-t pt-2 mt-2 space-y-1 text-xs text-slate-600">
              <p>Lead → Contactado: <strong>{embudo.tasas.leads_a_contactados || 0}%</strong></p>
              <p>Contactado → Pre Cal: <strong>{embudo.tasas.contactados_a_precal || 0}%</strong></p>
              <p>Pre Cal → Reunión: <strong>{embudo.tasas.precal_a_reunion || 0}%</strong></p>
              <p>Reunión → Cierre: <strong>{embudo.tasas.reunion_a_cierre || 0}%</strong></p>
              <p className="text-emerald-700 font-semibold">Cierre total: {embudo.tasas.cierre_total || 0}%</p>
            </div>
          </div>
        )}
      </Card>
    </div>
    </div>
  );
};

// ==================== CAMPAÑAS TAB ====================

const CampanasTab = ({ campanas, onChange }) => {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ nombre: '', plataforma: 'Meta', inversion_clp: 0, clics: 0, impresiones: 0, estado: 'activa' });
  const [editingId, setEditingId] = useState(null);

  const handleSave = async () => {
    if (form.nombre.trim().length < 2) { toast.error('Nombre requerido'); return; }
    try {
      if (editingId) {
        await api.put(`/marketing/campanas/${editingId}`, form);
        toast.success('Actualizada');
      } else {
        await api.post('/marketing/campanas', form);
        toast.success('Campaña creada');
      }
      setShowNew(false);
      setForm({ nombre: '', plataforma: 'Meta', inversion_clp: 0, clics: 0, impresiones: 0, estado: 'activa' });
      setEditingId(null);
      onChange();
    } catch (e) { toast.error('Error'); }
  };

  const handleEdit = (c) => {
    setForm({
      nombre: c.nombre, plataforma: c.plataforma, inversion_clp: c.inversion_clp || 0,
      clics: c.clics, impresiones: c.impresiones, estado: c.estado, notas: c.notas || ''
    });
    setEditingId(c.id);
    setShowNew(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar campaña?')) return;
    try {
      await api.delete(`/marketing/campanas/${id}`);
      toast.success('Eliminada');
      onChange();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Campañas publicitarias</h3>
        <Button className="bg-rose-500 hover:bg-rose-600" onClick={() => { setEditingId(null); setForm({ nombre: '', plataforma: 'Meta', inversion_clp: 0, clics: 0, impresiones: 0, estado: 'activa' }); setShowNew(true); }} data-testid="new-campana-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva campaña
        </Button>
      </div>

      {campanas.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">Sin campañas registradas</p>
          <p className="text-xs text-slate-500 mt-1">Crea una campaña con inversión y métricas para empezar a medir ROI.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campanas.map(c => (
            <Card key={c.id} className="p-4 border-slate-200" data-testid={`campana-card-${c.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-slate-900">{c.nombre}</h4>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{c.plataforma}</Badge>
                    <Badge className={`text-[10px] ${c.estado === 'activa' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>{c.estado}</Badge>
                    {c.meta_campaign_id && (
                      <Badge className="text-[10px] bg-blue-100 text-blue-700">
                        <Link2 className="w-2.5 h-2.5 mr-0.5" /> Meta sync
                      </Badge>
                    )}
                    {c.last_sync_at && (
                      <span className="text-[10px] text-slate-400">Últ. sync: {new Date(c.last_sync_at).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(c)} data-testid={`edit-campana-${c.id}`}>
                    <FileText className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} data-testid={`del-campana-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-slate-50 p-2 rounded border border-slate-200">
                  <p className="text-[10px] text-slate-500 uppercase">Inversión</p>
                  <p className="text-base font-bold text-slate-800">{fmtCLP(c.inversion_clp)}</p>
                </div>
                <div className="bg-slate-50 p-2 rounded border border-slate-200">
                  <p className="text-[10px] text-slate-500 uppercase">Leads / CPL</p>
                  <p className="text-base font-bold text-slate-800">{c.metricas.leads_total} · {fmtCLPCorto(c.metricas.cpl_clp)}</p>
                </div>
                <div className="bg-slate-50 p-2 rounded border border-slate-200">
                  <p className="text-[10px] text-slate-500 uppercase">CTR</p>
                  <p className="text-base font-bold text-slate-800">{c.metricas.ctr}%</p>
                </div>
                <div className="bg-emerald-50 p-2 rounded border border-emerald-200 col-span-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-emerald-700 uppercase">Conversiones</p>
                      <p className="text-base font-bold text-emerald-800">{fmtCLP(c.metricas.conversion_clp)} · {c.metricas.leads_aceptados} cliente(s)</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-emerald-700 uppercase">ROI</p>
                      <p className={`text-2xl font-bold ${c.metricas.roi_x >= 1 ? 'text-emerald-700' : 'text-rose-700'}`}>{c.metricas.roi_x}x</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg" data-testid="campana-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar campaña' : 'Nueva campaña'}</DialogTitle>
            <DialogDescription>
              Datos de inversión y métricas. Cuando Meta/Google estén integrados, estos campos se sincronizarán automáticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} data-testid="campana-nombre-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Plataforma</Label>
                <Select value={form.plataforma} onValueChange={(v) => setForm({...form, plataforma: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Meta">Meta (Facebook/Instagram)</SelectItem>
                    <SelectItem value="Google">Google Ads</SelectItem>
                    <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                    <SelectItem value="Web">Web propio</SelectItem>
                    <SelectItem value="Otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({...form, estado: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activa">Activa</SelectItem>
                    <SelectItem value="pausada">Pausada</SelectItem>
                    <SelectItem value="finalizada">Finalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Inversión (CLP)</Label><Input type="number" step="1" value={form.inversion_clp} onChange={(e) => setForm({...form, inversion_clp: parseFloat(e.target.value) || 0})} data-testid="campana-inversion-input" /></div>
              <div><Label>Clics</Label><Input type="number" value={form.clics} onChange={(e) => setForm({...form, clics: parseInt(e.target.value) || 0})} /></div>
              <div><Label>Impresiones</Label><Input type="number" value={form.impresiones} onChange={(e) => setForm({...form, impresiones: parseInt(e.target.value) || 0})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button className="bg-rose-500 hover:bg-rose-600" onClick={handleSave} data-testid="save-campana-btn">{editingId ? 'Guardar' : 'Crear'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== EMBUDO TAB ====================

const EmbudoTab = ({ embudo }) => {
  if (!embudo) return null;
  const max = Math.max(...ETAPAS.map(e => embudo.por_etapa[e] || 0), 1);
  return (
    <Card className="p-6 border-slate-200">
      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Target className="w-5 h-5 text-rose-500" /> Embudo de Conversión · {embudo.total_leads} prospectos totales
      </h3>
      <div className="space-y-4">
        {ETAPAS.map((e, idx) => {
          const cant = embudo.por_etapa[e] || 0;
          const width = max > 0 ? (cant / max) * 100 : 0;
          const col = ETAPA_COLOR[e];
          return (
            <div key={e} className="relative" data-testid={`funnel-row-${e.replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-3 mb-1">
                <span className={`w-7 h-7 rounded-full ${col.dot} text-white text-xs font-bold flex items-center justify-center`}>{idx + 1}</span>
                <h4 className="font-semibold text-slate-800">{e}</h4>
                <Badge variant="outline" className={`${col.bg} ${col.text} ${col.border} ml-auto`}>{cant} prospectos</Badge>
              </div>
              <div className="h-10 bg-slate-100 rounded-lg overflow-hidden ml-10">
                <div
                  className={`h-full ${col.dot} flex items-center justify-end pr-3 transition-all`}
                  style={{ width: `${Math.max(width, 4)}%` }}
                >
                  <span className="text-white text-xs font-bold">{cant}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-4 border-t border-slate-200">
        <Card className="p-3 bg-blue-50 border-blue-200">
          <p className="text-xs text-blue-700 font-semibold uppercase">Lead → Contactado</p>
          <p className="text-2xl font-bold text-blue-800">{embudo.tasas.leads_a_contactados || 0}%</p>
        </Card>
        <Card className="p-3 bg-violet-50 border-violet-200">
          <p className="text-xs text-violet-700 font-semibold uppercase">Contactado → Pre Cal</p>
          <p className="text-2xl font-bold text-violet-800">{embudo.tasas.contactados_a_precal || 0}%</p>
        </Card>
        <Card className="p-3 bg-indigo-50 border-indigo-200">
          <p className="text-xs text-indigo-700 font-semibold uppercase">Pre Cal → Reunión</p>
          <p className="text-2xl font-bold text-indigo-800">{embudo.tasas.precal_a_reunion || 0}%</p>
        </Card>
        <Card className="p-3 bg-purple-50 border-purple-200">
          <p className="text-xs text-purple-700 font-semibold uppercase">Reunión → Cierre</p>
          <p className="text-2xl font-bold text-purple-800">{embudo.tasas.reunion_a_cierre || 0}%</p>
        </Card>
        <Card className="p-3 bg-emerald-50 border-emerald-200">
          <p className="text-xs text-emerald-700 font-semibold uppercase">Cierre total</p>
          <p className="text-2xl font-bold text-emerald-800">{embudo.tasas.cierre_total || 0}%</p>
        </Card>
        <Card className="p-3 bg-rose-50 border-rose-200">
          <p className="text-xs text-rose-700 font-semibold uppercase">Tasa de descarte</p>
          <p className="text-2xl font-bold text-rose-800">{embudo.tasas.descarte_total || 0}%</p>
        </Card>
        <Card className="p-3 bg-teal-50 border-teal-200">
          <p className="text-xs text-teal-700 font-semibold uppercase">Recuperación</p>
          <p className="text-2xl font-bold text-teal-800">{embudo.tasas.recuperacion || 0}%</p>
        </Card>
      </div>
    </Card>
  );
};

// ==================== NUEVO PROSPECTO ====================

const NuevoProspectoButton = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', empresa: '', rut: '', mensaje: '', origen: 'Manual' });

  const handleSave = async () => {
    if (form.nombre.trim().length < 2) { toast.error('Nombre requerido'); return; }
    try {
      const res = await api.post('/marketing/prospectos', form);
      if (res.data.prospecto?.urgencia_critica) {
        toast.warning('⚠️ URGENCIA CRÍTICA detectada en el mensaje');
      } else {
        toast.success('Prospecto creado');
      }
      setOpen(false);
      setForm({ nombre: '', email: '', telefono: '', empresa: '', rut: '', mensaje: '', origen: 'Manual' });
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al crear prospecto');
    }
  };

  return (
    <>
      <Button className="bg-rose-500 hover:bg-rose-600" size="sm" onClick={() => setOpen(true)} data-testid="new-prospecto-btn">
        <Plus className="w-4 h-4 mr-1" /> Nuevo prospecto
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-prospecto-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo prospecto manual</DialogTitle>
            <DialogDescription>Para leads que llegan por canales no automáticos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Nombre *" value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} data-testid="new-prospecto-nombre" />
            <Input placeholder="Empresa" value={form.empresa} onChange={(e) => setForm({...form, empresa: e.target.value})} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
              <Input placeholder="Teléfono" value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} />
            </div>
            <Input placeholder="RUT" value={form.rut} onChange={(e) => setForm({...form, rut: e.target.value})} />
            <Textarea rows={3} placeholder="Mensaje / consulta inicial (la IA detectará urgencia)" value={form.mensaje} onChange={(e) => setForm({...form, mensaje: e.target.value})} />
            <Select value={form.origen} onValueChange={(v) => setForm({...form, origen: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Manual">Manual</SelectItem>
                <SelectItem value="Referido">Referido</SelectItem>
                <SelectItem value="Web">Web propio</SelectItem>
                <SelectItem value="Meta">Meta (manual)</SelectItem>
                <SelectItem value="Google">Google (manual)</SelectItem>
                <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                <SelectItem value="Evento">Evento</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-rose-500 hover:bg-rose-600" onClick={handleSave} data-testid="save-prospecto-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Marketing;
